import { Injectable } from '@angular/core';
import { API_ERRORS, IHttpResponse, ITelegramUserFrom, ITokens, SIGN_PROVIDERS } from '@libs/interfaces';
import { catchError, of, map, tap, Observable, switchMap } from 'rxjs';
import { AuthService } from '@libs/app.services/landing/auth.service';
import { ApiService } from '@libs/app.services/api.service';
import { MODAL_TYPE } from '@libs/store/toast/model';
import { addToast } from '@libs/store/toast/actions';
import { Store } from '@ngrx/store';
import { ConfigService } from '@libs/app.services/landing/config.service';
import { LocalstorageService } from '../landing/localstorage.service';
import { DeviceService } from '../device.service';

// startOnNotRegistered {
//      id: 647898320,
//      is_bot: false,
//      first_name: 'José Manuel',
//      last_name: 'Villar Alarcón',
//      username: 'jmvalarcon',
//      language_code: 'es'
//    }
// ?telegramUserId=647898320&first_name=José%20Manuel&last_name=Villar%20Alarcón&username=jmvalarcon&language_code=es
@Injectable()
export class SignService {
  constructor(
    private apiService: ApiService,
    private auth: AuthService,
    private ls: LocalstorageService,
    private store: Store,
    private config: ConfigService,
    private deviceService: DeviceService,
  ) {}

  /**
   * Generate a code for the given value.
   * @param {string} value - The value to generate the code for.
   * @returns {Observable<any>} An observable containing the generated code or an error response.
   */
  generateCode(value: string) {
    return this.apiService
      .get(`auth/generateCode/${value}`)
      .pipe(catchError(() => of({ error: API_ERRORS.ERROR_RESPONSE })));
  }

  /**
   * Create a temporary user account.
   * @param {any} account - The account information.
   * @returns {Observable<any>} An observable containing the result of the account creation.
   */
  createTmpUser(account: any): Observable<any> {
    const telegramUser: ITelegramUserFrom = this.deviceService.telegramUser();
    let payload;
    if (telegramUser) {
      payload = {
        ...account,
        provider: { ...telegramUser, provider: SIGN_PROVIDERS.TELEGRAM },
      };
    } else {
      payload = { ...account };
    }
    return this.apiService
      .post(`user/create/temp`, payload)
      .pipe(tap((result: IHttpResponse) => result.result && this.ls.storeEmail(account.email)));
  }

  /**
   * Check the provided code for the given value.
   * @param {string} value - The value to check the code for.
   * @param {string} code - The code to check.
   * @returns {Observable<any>} An observable containing the result of the code check.
   */
  checkCode(value: string, code: string): Observable<any> {
    return this.apiService
      .post(`auth/onetimeCode`, {
        value: value,
        code: code,
        app: this.ls.app,
        provider: this.deviceService.telegramUser(),
      })
      .pipe(
        catchError(() => of({ error: API_ERRORS.ERROR_RESPONSE })),
        map((result: IHttpResponse) => this.onTokens(result)),
        switchMap((result: { error: any }) => (result?.error ? this.onCheckError(result.error) : this.onCheckOk())),
      );
  }

  /**
   * Handle the error that occurs during the code check process.
   * @param {string} error - The error message.
   * @returns {Observable<any>} An observable containing the error.
   */
  onCheckError(error: string) {
    const toast = {
      id: Date.now(),
      info: 'LANDING.YHBLOA',
      type: MODAL_TYPE.WARNING,
    };
    this.store.dispatch(addToast({ toast }));
    return of({ error });
  }

  /**
   * Handle the successful code check process.
   * @returns {Observable<any>} An observable that initializes the authentication process and navigates to the landing page.
   */
  onCheckOk() {
    return this.config.init();
  }

  /**
   * Handle the tokens received from the server.
   * @param {IHttpResponse} result - The response containing the tokens.
   * @returns {IHttpResponse} The response containing any errors.
   */
  private onTokens(result): { error: any } {
    if (result?.data?.error) return result.data;
    const tokens: ITokens = result.data;
    this.auth.storeTokens(tokens);
    this.auth.init();
    return { error: '' };
  }
}
